import { useState } from 'react';
import { useMutation } from '@apollo/client';

import { useStickyState } from '@crehana/react-hooks';

import SEND_MESSAGE from 'Jsx/CrehanaLive/graphql/SendLiveMessage.graphql';
import {
  SendLiveMessage,
  SendLiveMessageVariables,
} from 'Jsx/CrehanaLive/graphql/types/SendLiveMessage';
import SET_MESSAGE_AS_QUESTION from 'Jsx/CrehanaLive/graphql/SetMessageAsQuestion.graphql';
import {
  SetMessageAsQuestion,
  SetMessageAsQuestionVariables,
} from 'Jsx/CrehanaLive/graphql/types/SetMessageAsQuestion';
import PIN_MESSAGE from 'Jsx/CrehanaLive/graphql/PinLiveMessage.graphql';
import {
  PinLiveMessage,
  PinLiveMessageVariables,
} from 'Jsx/CrehanaLive/graphql/types/PinLiveMessage';
import UNPIN_MESSAGE from 'Jsx/CrehanaLive/graphql/RemovePinLiveMessage.graphql';
import {
  RemovePinLiveMessage,
  RemovePinLiveMessageVariables,
} from 'Jsx/CrehanaLive/graphql/types/RemovePinLiveMessage';
import BLOCK_LIVE_USER from 'Jsx/CrehanaLive/graphql/BlockLiveUser.graphql';
import {
  BlockLiveUser,
  BlockLiveUserVariables,
} from 'Jsx/CrehanaLive/graphql/types/BlockLiveUser';
import HIDE_MESSAGE from 'Jsx/CrehanaLive/graphql/HideLiveMessage.graphql';
import {
  HideLiveMessage,
  HideLiveMessageVariables,
} from 'Jsx/CrehanaLive/graphql/types/HideLiveMessage';
import LIKE_LIVE_QUESTION from 'Jsx/CrehanaLive/graphql/LikeLiveQuestion.graphql';
import {
  LikeLiveQuestion,
  LikeLiveQuestionVariables,
} from 'Jsx/CrehanaLive/graphql/types/LikeLiveQuestion';

import { LiveNode } from '../graphql/types/LiveNode';

export interface ChatActions {
  sendMessage: (message: string) => void;
  setMessageAsQuestion: (messageId: string, isQuestion: boolean) => void;
  hideMessage: (messageId: string) => void;
  blockUser: (userId: string) => void;
  setPinnedMessage: (messageId: string) => void;
  unpinMessage: (messageId: string) => void;
  likeLiveQuestion: (messageId: string) => void;
  isChatBlocked: boolean;
  likedMessages: string[];
}

const USER_NOT_ALLOWED = 1324;

function useChatActions(live: LiveNode): ChatActions {
  const [likedMessages, setLikedMessages] = useStickyState<string[]>(
    [],
    'LIVE_LKED_MESSAGES',
  );
  const [isChatBlocked, setIsChatBlocked] = useState(false);

  const [sendMessageMutation] =
    useMutation<SendLiveMessage, SendLiveMessageVariables>(SEND_MESSAGE);
  const [setMessageAsQuestionMutation] = useMutation<
    SetMessageAsQuestion,
    SetMessageAsQuestionVariables
  >(SET_MESSAGE_AS_QUESTION);
  const [setPinnedLiveMessage] =
    useMutation<PinLiveMessage, PinLiveMessageVariables>(PIN_MESSAGE);
  const [unpinLiveMessage] =
    useMutation<RemovePinLiveMessage, RemovePinLiveMessageVariables>(
      UNPIN_MESSAGE,
    );
  const [hideLiveMessage] =
    useMutation<HideLiveMessage, HideLiveMessageVariables>(HIDE_MESSAGE);
  const [blockLiveUser] =
    useMutation<BlockLiveUser, BlockLiveUserVariables>(BLOCK_LIVE_USER);
  const [likeLiveQestionMutation] =
    useMutation<LikeLiveQuestion, LikeLiveQuestionVariables>(
      LIKE_LIVE_QUESTION,
    );

  const sendMessage = (message: string) => {
    const trimmed = message.trim();

    if (trimmed && live.channel) {
      sendMessageMutation({
        variables: {
          inboxId: live.channel,
          // message: message.replace(/(?:\r\n|\r|\n)/g, '<br />'),
          message: trimmed,
        },
      }).then(({ data }) => {
        if (
          data?.chatInLive?.success === false &&
          data.chatInLive.response?.code === USER_NOT_ALLOWED
        ) {
          setIsChatBlocked(true);
        }
      });
    }
  };

  const setMessageAsQuestion = (messageId: string, isQuestion: boolean) => {
    setMessageAsQuestionMutation({ variables: { messageId, isQuestion } });
  };

  const hideMessage = (messageId: string) => {
    hideLiveMessage({ variables: { chatId: messageId } });
  };

  const blockUser = (userId: string) => {
    blockLiveUser({
      variables: { liveId: live.originalId!.toString(), userId },
    });
  };

  const setPinnedMessage = (messageId: string) => {
    setPinnedLiveMessage({ variables: { messageId } });
  };

  const unpinMessage = (messageId: string) => {
    unpinLiveMessage({ variables: { messageId } });
  };

  const likeLiveQuestion = (messageId: string) => {
    likeLiveQestionMutation({ variables: { messageId } }).then(({ data }) => {
      if (data?.likeChatMessageInLive?.success) {
        setLikedMessages(prev =>
          prev.includes(messageId)
            ? prev.filter(p => p !== messageId)
            : prev.concat(messageId),
        );
      }
    });
  };

  return {
    likedMessages,
    isChatBlocked,
    sendMessage,
    setMessageAsQuestion,
    hideMessage,
    blockUser,
    setPinnedMessage,
    unpinMessage,
    likeLiveQuestion,
  };
}

export default useChatActions;
